/**
 * Time Frame detail page — shows the frame's current rules, evaluator
 * status ("Currently in frame" / "Currently not in frame"), and lets
 * the user edit or delete from here.
 */
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { timeFrames, formatTimeFrameRef } from '@/api/timeFrames';
import type { TimeFrameRow, DaysOfWeekSlot, SpecificDateRange } from '@/api/timeFrames';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useToast } from '@/hooks/use-toast';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Pencil, Trash2, ArrowLeft } from 'lucide-react';
import { TimeFrameDialog } from './TimeFrameDialog';

const CAN_MUTATE = ['super_admin', 'reseller_owner', 'office_manager'];
const DAY_LABELS: Record<string, string> = {
  sun: 'Sunday', mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday',
  thu: 'Thursday', fri: 'Friday', sat: 'Saturday',
};
const DAY_ORDER: string[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

export function TimeFrameDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const user = useCurrentUser();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const canMutate = CAN_MUTATE.includes(user.role);

  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const frameQ = useQuery({
    queryKey: ['timeFrames', id],
    queryFn: () => timeFrames.get(id),
  });

  const evalQ = useQuery({
    queryKey: ['timeFrames', id, 'evaluate'],
    queryFn: () => timeFrames.evaluate(id),
    refetchInterval: 30_000, // refresh the badge every 30s so "Currently …" stays honest
  });

  const [blockingError, setBlockingError] = useState<import('@/api/timeFrames').TimeFrameInUseBody | null>(null);

  const remove = useMutation({
    mutationFn: () => timeFrames.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['timeFrames'] });
      toast({ title: 'Time frame deleted' });
      navigate('/time-frames');
    },
    onError: (err: unknown) => {
      const e = err as Error & { status?: number; code?: string; details?: unknown };
      if (e.status === 409 && e.code === 'time_frame_in_use') {
        const body = e.details as import('@/api/timeFrames').TimeFrameInUseBody | undefined;
        if (body?.blocking_nodes) {
          setBlockingError(body);
          setDeleteOpen(false);
          return;
        }
      }
      toast({ title: 'Could not delete', description: e.message || 'Unknown', variant: 'destructive' });
    },
  });

  const frame = frameQ.data;

  if (frameQ.isLoading) {
    return <div className="p-6 text-muted-foreground">Loading…</div>;
  }
  if (!frame) {
    return (
      <div className="p-6 space-y-3">
        <p className="text-muted-foreground">Time frame not found.</p>
        <Button variant="outline" onClick={() => navigate('/time-frames')}>
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to time frames
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title={frame.name}
        eyebrow={(
          <span>
            <button
              type="button"
              onClick={() => navigate('/time-frames')}
              className="hover:underline underline-offset-2"
            >
              Time Frames
            </button>
            {' · '}
            <span className="font-mono">{formatTimeFrameRef(frame.ref_number)}</span>
          </span>
        )}
        actions={(
          <div className="flex items-center gap-2">
            {evalQ.data && (
              <Badge variant={evalQ.data.result === 'in_frame' ? 'default' : 'secondary'}>
                Currently {evalQ.data.result === 'in_frame' ? 'in frame' : 'not in frame'}
              </Badge>
            )}
            {canMutate && (
              <>
                <Button variant="outline" onClick={() => setEditOpen(true)}>
                  <Pencil className="h-4 w-4 mr-1.5" /> Edit
                </Button>
                <Button variant="outline" onClick={() => setDeleteOpen(true)}>
                  <Trash2 className="h-4 w-4 mr-1.5" /> Delete
                </Button>
              </>
            )}
          </div>
        )}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <Row label="Mode">
            {frame.mode === 'always' && 'Always'}
            {frame.mode === 'days_of_week' && 'Days of the week and times'}
            {frame.mode === 'specific_dates' && 'Specific dates or ranges'}
          </Row>
          <Row label="Timezone"><span className="font-mono">{frame.timezone}</span></Row>
          <Row label="Active">
            {frame.active
              ? <Badge variant="default">Active</Badge>
              : <Badge variant="secondary">Inactive</Badge>}
          </Row>
          <Row label="Owner">
            {frame.created_by_name
              ? <span>{frame.created_by_name}</span>
              : <span className="text-muted-foreground italic">No owner recorded</span>}
          </Row>
          <Row label="Ref"><span className="font-mono">{formatTimeFrameRef(frame.ref_number)}</span></Row>
        </CardContent>
      </Card>

      {frame.mode === 'days_of_week' && (
        <DaysOfWeekDetail frame={frame} />
      )}
      {frame.mode === 'specific_dates' && (
        <SpecificDatesDetail frame={frame} />
      )}

      <TimeFrameDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        initial={frame}
      />

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this time frame?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            <strong>{frame.name}</strong> will be soft-deleted. If any IVR
            nodes still reference it, we'll block the delete and show you
            which ones first.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={() => remove.mutate()} disabled={remove.isPending}>
              {remove.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!blockingError} onOpenChange={(o) => { if (!o) setBlockingError(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cannot delete — still in use</DialogTitle>
          </DialogHeader>
          <div className="text-sm space-y-3">
            <p className="text-muted-foreground">
              This time frame is referenced by the following IVR nodes.
              Detach it from each node before deleting.
            </p>
            <ul className="border rounded-md divide-y">
              {blockingError?.blocking_nodes.map((n) => (
                <li key={n.id} className="px-3 py-2 flex items-center gap-3">
                  <Badge variant="outline" className="font-mono text-xs">IVR-{n.ref_number}</Badge>
                  <span className="flex-1 font-medium">{n.name}</span>
                  <span className="text-xs text-muted-foreground font-mono">{n.node_type}</span>
                </li>
              ))}
            </ul>
          </div>
          <DialogFooter>
            <Button onClick={() => setBlockingError(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 items-center">
      <div className="text-muted-foreground text-xs">{label}</div>
      <div>{children}</div>
    </div>
  );
}

function DaysOfWeekDetail({ frame }: { frame: TimeFrameRow }) {
  const slots = (frame.rules as { slots?: DaysOfWeekSlot[] }).slots ?? [];
  const byDay = useMemo<Record<string, DaysOfWeekSlot[]>>(() => {
    const m: Record<string, DaysOfWeekSlot[]> = {};
    for (const s of slots) {
      if (!m[s.day]) m[s.day] = [];
      m[s.day]!.push(s);
    }
    return m;
  }, [slots]);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Weekly schedule</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {DAY_ORDER.map((d) => (
          <div key={d} className="flex items-baseline gap-3">
            <div className="w-24 text-sm font-medium">{DAY_LABELS[d]}</div>
            <div className="flex-1 text-muted-foreground">
              {byDay[d]?.length
                ? byDay[d]!.map((s, i) => (
                  <span key={i}>
                    {i > 0 && ', '}
                    <span className="font-mono">{s.from}–{s.to}</span>
                  </span>
                ))
                : <span className="italic">Closed</span>}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function SpecificDatesDetail({ frame }: { frame: TimeFrameRow }) {
  const ranges = (frame.rules as { ranges?: SpecificDateRange[] }).ranges ?? [];
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Date ranges</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {ranges.length === 0
          ? <div className="text-muted-foreground italic">No ranges configured.</div>
          : ranges.map((r, i) => (
            <div key={i} className="font-mono text-sm">
              {r.from} <span className="text-muted-foreground">to</span> {r.to}
            </div>
          ))}
      </CardContent>
    </Card>
  );
}
